import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { AdminOnly } from '@/components/auth/AdminOnly';
import { useEventsFeed, useUsers, useWorkers } from '@/hooks/use-fleet';
import {
  DEFAULT_TASK_RANGE,
  DEFAULT_TASK_STATUS,
  isTaskRange,
  isTaskStatusFilter,
  type TaskRange,
  type TaskStatusFilter,
} from '@/lib/fleet';
import type { SortSearch } from '@/lib/sorting';
import { RootLayout } from '@/routes/__root';
import { AboutPage } from '@/routes/about';
import { AdminPage } from '@/routes/admin';
import { AdminAuditPage } from '@/routes/admin-audit';
import { AdminReposPage } from '@/routes/admin-repos';
import { AgentsPage } from '@/routes/agents';
import { BriefsPage } from '@/routes/briefs';
import { HomePage } from '@/routes/home';
import { JobPage, type JobSearch } from '@/routes/job';
import { GRAPH_ANCHOR } from '@/routes/job-graph';
import { JoinPage } from '@/routes/join';
import { MePage } from '@/routes/me';
import {
  DEFAULT_METRICS_RANGE,
  isMetricsRange,
  MetricsPage,
  type MetricsRange,
} from '@/routes/metrics';
import { PeoplePage } from '@/routes/people';
import { ProjectPage } from '@/routes/project';
import { SchedulesPage } from '@/routes/schedules';
import { TaskPage } from '@/routes/task';

/**
 * Code-based routes, as the core-template has them.
 *
 * The tree changed shape in the redesign. Four destinations where there were
 * six: `/review` folded into `/` because it was a filter on the gate list rather
 * than a place, and the two admin links became an `/admin` section with a front
 * door of its own. `/fleet` became `/agents` and stopped being admin-only, since
 * a member has devices too and hiding the whole idea from them hid the most
 * surprising fact about the product.
 *
 * Every path that went away is a redirect rather than a 404. People send each
 * other links to a plan waiting for approval, and a link somebody saved in
 * Slack last week has to keep meaning the same thing.
 *
 * A selection is still a route rather than component state (`/jobs/:id`, and
 * `/jobs`'s own `?task=` for the task board) for exactly that reason.
 */
const rootRoute = createRootRoute({ component: RootLayout });

/**
 * `?focus=<id>` on the pages that are tables rather than routes.
 *
 * The palette can point at a person or a device, and neither has a page of its
 * own, so the row is named in the search string instead. Validated rather than
 * left to pass through, because it is a contract the palette writes and
 * `useFocusedRow` reads, and a contract worth one function.
 */
function focusSearch(search: Record<string, unknown>): { focus?: string } {
  return typeof search.focus === 'string' && search.focus ? { focus: search.focus } : {};
}

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
});

/**
 * The task board's filters, from when `/tasks` was a page of its own.
 *
 * The board lives under Jobs now (`JobsSearch`, below), but `/tasks` still
 * parses these so a bookmarked filtered link keeps meaning the same thing on
 * the way through the redirect. Anything unrecognised is dropped rather than
 * defaulted loudly: a hand-edited `?status=nonsense` should show the whole
 * board, not an error.
 */
export interface TasksSearch {
  repo?: string;
  status?: TaskStatusFilter;
  range?: TaskRange;
}

function validateTasksSearch(search: Record<string, unknown>): TasksSearch {
  const out: TasksSearch = {};
  if (typeof search.repo === 'string' && search.repo) out.repo = search.repo;
  if (isTaskStatusFilter(search.status) && search.status !== DEFAULT_TASK_STATUS) {
    out.status = search.status;
  }
  if (isTaskRange(search.range) && search.range !== DEFAULT_TASK_RANGE) out.range = search.range;
  return { ...out, ...sortSearchOf(search) };
}

/**
 * `/tasks`, the list, is retired but not gone (issue #60): the task board is a
 * view on `/jobs` now, reached from the one nav entry for work, and this
 * redirects into it rather than 404 so a saved link keeps working. The filters
 * a bookmarked link carried are parsed here and forwarded.
 */
const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  validateSearch: validateTasksSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/jobs', search: { ...search, board: true }, replace: true });
  },
  component: () => null,
});

/**
 * A task is a page of its own (issue #61), at the path it has always had.
 *
 * Issue #60 pointed this one at the board too, because at the time there was
 * nothing else at the other end: the path rendered the same 400px pane the
 * board pops out at the side, so forwarding to the board cost a saved link
 * nothing. There is a page now, and `/tasks/:taskId` is where it lives - the
 * path every link in the console already carries (the palette, the gate stack,
 * a dependency edge, the board's own peek) and the one people have in their
 * Slack history. Reusing it is what makes each of those land on the page
 * rather than on a redirect that drops them back where they came from.
 *
 * The list above stays a redirect: a list of tasks *is* the board, and there
 * is one nav entry for work. Only the singular is a destination.
 *
 * No `validateSearch`, unlike the list: this route reads nothing from the
 * search string, and an old link carrying the board's filters (`?status=...`)
 * should land on the task rather than be met with an error about a param the
 * page has no use for.
 */
const taskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks/$taskId',
  component: TaskPage,
});

/**
 * A sortable route has to declare `sort` and `dir`, or the router strips them on
 * the first navigation and the control appears to do nothing (issue #74). The
 * values are not validated against a page's keys here: `readSort` falls back to
 * the page's default for a key it does not have, which keeps an old link
 * working, and this file has no business knowing what any page can sort by.
 */
function sortSearchOf(search: Record<string, unknown>): SortSearch {
  const out: SortSearch = {};
  if (typeof search.sort === 'string' && search.sort) out.sort = search.sort;
  if (search.dir === 'asc' || search.dir === 'desc') out.dir = search.dir;
  return out;
}

/** `focusSearch` plus `sortSearchOf`, for the routes that carry both a palette
 * focus and a sort (issue #74's `/agents` and `/admin/people`). */
function focusAndSortSearch(search: Record<string, unknown>): { focus?: string } & SortSearch {
  return { ...focusSearch(search), ...sortSearchOf(search) };
}

/**
 * Jobs, plus the task board it now carries (issue #60): one nav entry for
 * work rather than two, "Jobs" and the old, separate "Steps".
 *
 * `board` switches the page from the jobs list to the flat task board; `task`
 * is the board's own selection, an overview shown beside it rather than a
 * navigation away, same shape as `JobSearch.step` on a job's own page. `repo`,
 * `status` and `range` are the board's filters, carried over from when it was
 * `/tasks`'s own `TasksSearch` (still parsed there for old links; see below).
 * The jobs list's own sort and the board's share `sort`/`dir`: only one view
 * is ever on screen, so there is no ambiguity in reusing the pair, and an old
 * link naming a key the other view does not have simply falls back to that
 * view's default (`readSort`).
 *
 * `board` is `true`, `false` or absent, not just present-or-not (issue #98):
 * the page opens on the board by default now, remembered per viewer, and
 * `undefined` here means "no opinion in the URL, use the remembered choice"
 * where `false` means "the list, for this visit specifically" -- a link
 * carrying `?board=false` has to keep landing on the list even for a viewer
 * whose stored preference is the board.
 */
export interface JobsSearch extends SortSearch {
  board?: boolean;
  task?: string;
  repo?: string;
  status?: TaskStatusFilter;
  range?: TaskRange;
}

function validateJobsSearch(search: Record<string, unknown>): JobsSearch {
  const out: JobsSearch = {};
  if (search.board === true || search.board === 'true') out.board = true;
  else if (search.board === false || search.board === 'false') out.board = false;
  if (typeof search.task === 'string' && search.task) out.task = search.task;
  if (typeof search.repo === 'string' && search.repo) out.repo = search.repo;
  if (isTaskStatusFilter(search.status) && search.status !== DEFAULT_TASK_STATUS) {
    out.status = search.status;
  }
  if (isTaskRange(search.range) && search.range !== DEFAULT_TASK_RANGE) out.range = search.range;
  return { ...out, ...sortSearchOf(search) };
}

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs',
  component: BriefsPage,
  validateSearch: validateJobsSearch,
});

/**
 * A job is a page of its own, not a 400px rail beside the list.
 *
 * `?step=<taskId>` is the step whose detail is open on it, which is a selection
 * and therefore lives in the URL like every other selection in the console.
 * Anything else in the search string is dropped rather than defaulted loudly.
 */
function validateJobSearch(search: Record<string, unknown>): JobSearch {
  return typeof search.step === 'string' && search.step ? { step: search.step } : {};
}

const jobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$briefId',
  component: JobPage,
  validateSearch: validateJobSearch,
});

/**
 * The graph used to be a page of its own (docs/JOB-LIFECYCLE.md 2.8), because
 * the job lived in a 400px pane and a DAG does not fit one. The job is a page
 * now and the graph is a region on it, so both of these paths are deep links
 * into that page rather than destinations: the plain one lands on the graph's
 * anchor, and the one carrying a step opens that step there.
 *
 * Redirects rather than 404s, for the reason every retired path here is one:
 * people send each other links to a step they want a second opinion on.
 */
const jobGraphRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$briefId/graph',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/jobs/$briefId', params, hash: GRAPH_ANCHOR, replace: true });
  },
  component: () => null,
});

const jobGraphTaskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$briefId/graph/$taskId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/jobs/$briefId',
      params: { briefId: params.briefId },
      search: { step: params.taskId },
      hash: GRAPH_ANCHOR,
      replace: true,
    });
  },
  component: () => null,
});

/**
 * Schedules, under Work beside Jobs (docs/JOB-LIFECYCLE.md 2.7). The selection
 * is a route for the same reason a job's is: a schedule that paused itself is a
 * link somebody sends to the person who approved it.
 */
const schedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedules',
  component: SchedulesPage,
  validateSearch: sortSearchOf,
});

const scheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedules/$scheduleId',
  component: SchedulesPage,
  validateSearch: sortSearchOf,
});

const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agents',
  validateSearch: focusAndSortSearch,
  component: AgentsPage,
});

/**
 * `/project/:name` carries two sortable lists (its steps and the jobs naming
 * it), so it needs two pairs of sort params rather than the one `sortSearchOf`
 * gives every other sortable page (issue #74): one control's choice would
 * otherwise overwrite the other's in the URL.
 */
export interface ProjectSearch {
  stepSort?: string;
  stepDir?: 'asc' | 'desc';
  jobSort?: string;
  jobDir?: 'asc' | 'desc';
}

function validateProjectSearch(search: Record<string, unknown>): ProjectSearch {
  const out: ProjectSearch = {};
  if (typeof search.stepSort === 'string' && search.stepSort) out.stepSort = search.stepSort;
  if (search.stepDir === 'asc' || search.stepDir === 'desc') out.stepDir = search.stepDir;
  if (typeof search.jobSort === 'string' && search.jobSort) out.jobSort = search.jobSort;
  if (search.jobDir === 'asc' || search.jobDir === 'desc') out.jobDir = search.jobDir;
  return out;
}

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/project/$repo',
  component: ProjectPage,
  validateSearch: validateProjectSearch,
});

/**
 * `?days=30&repo=nymbl-app&person=sam@nymbl.app&sort=cost&dir=asc` on the
 * metrics page: the whole state of the view, so a slice somebody found is a
 * link they can paste into Slack and a reload lands back on it.
 *
 * The hub clamps `days` to 1..90 as well; validating here keeps the range
 * buttons and the URL in agreement, and drops anything hand-typed that is not
 * a range the page offers. `repo` and `person` are passed through as free text
 * rather than checked against a list, the way `validateProjectSearch` above
 * treats its sort keys: the options come from the response the URL is used to
 * fetch, so there is nothing to check them against yet, and the hub answers a
 * filter that matches nothing with an honest empty window.
 *
 * A key whose value is the default is left out, so a link points at the list
 * rather than at the state of a control the reader never touched.
 */
export interface MetricsSearch {
  days?: MetricsRange;
  repo?: string;
  person?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
}

function validateMetricsSearch(search: Record<string, unknown>): MetricsSearch {
  const out: MetricsSearch = {};
  const days = Number(search.days);
  if (isMetricsRange(days) && days !== DEFAULT_METRICS_RANGE) out.days = days;
  if (typeof search.repo === 'string' && search.repo) out.repo = search.repo;
  if (typeof search.person === 'string' && search.person) out.person = search.person;
  if (typeof search.sort === 'string' && search.sort) out.sort = search.sort;
  if (search.dir === 'asc' || search.dir === 'desc') out.dir = search.dir;
  return out;
}

const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/metrics',
  component: MetricsPage,
  validateSearch: validateMetricsSearch,
});

/**
 * The admin section.
 *
 * Every page in it earns its own read as its gate, so one request answers both
 * "may I see this" and "what is on it". The refusal is what `AdminOnly` renders,
 * which is why the query it is handed is left enabled rather than disabled for a
 * member: the 403 is the content.
 */
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: () => (
    <AdminOnly what="The admin section" useGate={useWorkers}>
      <AdminPage />
    </AdminOnly>
  ),
});

const adminPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/people',
  validateSearch: focusAndSortSearch,
  component: () => (
    <AdminOnly what="People" useGate={useUsers}>
      <PeoplePage />
    </AdminOnly>
  ),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit',
  validateSearch: sortSearchOf,
  component: () => (
    <AdminOnly what="The audit log" useGate={useEventsFeed}>
      <AdminAuditPage />
    </AdminOnly>
  ),
});

/**
 * Registering or disabling a repo is admin-only, but `GET /api/repos` is not -
 * the job composer's picker reads it too, for anybody signed in - so it cannot
 * serve as this route's own gate the way `useWorkers`/`useUsers` do for their
 * pages: a member would never see a 403 to trigger the refusal panel.
 * `useWorkers` stands in as the actually-enforced admin read, exactly as the
 * overview tab above uses it for the same reason.
 */
const adminReposRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/repos',
  component: () => (
    <AdminOnly what="Repos" useGate={useWorkers}>
      <AdminReposPage />
    </AdminOnly>
  ),
});

const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: MePage,
});

/**
 * The page an enrollment link opens: `/join?code=...`.
 *
 * `/join`, not `/enroll`, because `/enroll` is the worker's own POST surface on
 * the server and is reserved (docs/ARCHITECTURE.md) - a client route there
 * would never be reached. The code is read from the query and never written
 * anywhere: the page draws the steps and the laptop spends it.
 */
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join',
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === 'string' && search.code.trim() !== ''
      ? { code: search.code.trim() }
      : {},
  component: function Join() {
    const { code } = joinRoute.useSearch();
    return <JoinPage code={code ?? null} />;
  },
});

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
});

/**
 * The paths the redesign retired, kept alive.
 *
 * `/review` is the interesting one: it is not a rename, it is a page that no
 * longer exists, because pull request reviews are gates and gates are all on the
 * home page now. A saved link to it still lands somewhere that answers the same
 * question, which is the promise a redirect makes.
 */
function movedTo(from: string, to: string) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: from,
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
    component: () => null,
  });
}

/** The same, for a path that carries an id through to its new home. */
const movedBriefRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/briefs/$briefId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/jobs/$briefId', params, replace: true });
  },
  component: () => null,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  tasksRoute,
  taskRoute,
  jobsRoute,
  jobRoute,
  jobGraphRoute,
  jobGraphTaskRoute,
  schedulesRoute,
  scheduleRoute,
  agentsRoute,
  projectRoute,
  metricsRoute,
  adminRoute,
  adminPeopleRoute,
  adminReposRoute,
  adminAuditRoute,
  meRoute,
  joinRoute,
  aboutRoute,
  movedTo('/briefs', '/jobs'),
  movedBriefRoute,
  movedTo('/review', '/'),
  movedTo('/fleet', '/agents'),
  movedTo('/people', '/admin/people'),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
